import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  AutoAwesome as AutoAwesomeIcon,
  Psychology as PsychologyIcon,
  DinnerDining as DinnerDiningIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { get } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import Layout from "../../layout/Main/Layout";

const DietSystemOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [configurations, setConfigurations] = useState([]);
  const [systemStatus, setSystemStatus] = useState({});

  useEffect(() => {
    fetchSystemOverview();
  }, []);

  const fetchSystemOverview = async () => {
    try {
      setLoading(true);
      
      // Fetch diet configuration statistics
      const configStats = await get("/api/dashboard/diet-configurations/stats");
      
      // Fetch recent configurations
      const recentConfigs = await get("/api/dashboard/diet-configurations?page=1&limit=5");
      
      // Fetch system status (cron jobs, etc.)
      const status = await get("/api/dashboard/diet-configurations/stats");
      
      if (configStats?.success) {
        setStats(configStats.data || {});
      }
      
      if (recentConfigs?.success) {
        setConfigurations(recentConfigs.data || []);
      }
      
      setSystemStatus({
        cronActive: true, // This would come from actual cron status endpoint
        lastRun: new Date().toISOString(),
        nextRun: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Next day
      });
      
    } catch (error) {
      console.error("Error fetching system overview:", error);
      toastMessage("Failed to fetch system overview", "error");
    } finally {
      setLoading(false);
    }
  };

  const getDietTypeColor = (type) => {
    switch (type) {
      case "low_fodmap":
        return "primary";
      case "traditional":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case "elimination":
        return "error";
      case "introduction":
        return "warning";
      case "maintenance":
        return "success";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4">Diet Configuration System Overview</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => navigate("/diet-configurations")}
            >
              Manage Configurations
            </Button>
            <Button
              variant="outlined"
              startIcon={<DinnerDiningIcon />}
              onClick={() => navigate("/diet-prompts")}
            >
              Manage Prompts
            </Button>
          </Box>
        </Box>

        {/* System Status Alert */}
        <Alert 
          severity={systemStatus.cronActive ? "success" : "warning"} 
          sx={{ mb: 3 }}
          icon={systemStatus.cronActive ? <CheckCircleIcon /> : <WarningIcon />}
        >
          {systemStatus.cronActive 
            ? "Phase transition automation is active and running daily at 2:00 AM"
            : "Phase transition automation is currently inactive"
          }
        </Alert>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Configurations
                </Typography>
                <Typography variant="h4">{stats.total || 0}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Active: {stats.active || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Traditional Diets
                </Typography>
                <Typography variant="h4">
                  {stats.summary?.traditional?.count || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Active: {stats.summary?.traditional?.activeCount || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Low FODMAP Diets
                </Typography>
                <Typography variant="h4">
                  {stats.summary?.lowFodmap?.count || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Active: {stats.summary?.lowFodmap?.activeCount || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Next Cron Run
                </Typography>
                <Typography variant="h6">
                  {systemStatus.nextRun ? new Date(systemStatus.nextRun).toLocaleDateString() : "N/A"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Daily at 2:00 AM
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate("/diet-configurations")}
                  fullWidth
                >
                  Configure Diet Types & Phases
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DinnerDiningIcon />}
                  onClick={() => navigate("/diet-prompts")}
                  fullWidth
                >
                  Manage Diet Prompts
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TimelineIcon />}
                  onClick={() => navigate("/low-fodmap-diet")}
                  fullWidth
                >
                  View Low FODMAP Progress
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                System Features
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AutoAwesomeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Automatic Phase Transitions" 
                    secondary="Users automatically progress through diet phases based on time"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PsychologyIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Smart Prompt Selection" 
                    secondary="AI prompts automatically selected based on diet type and phase"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TimelineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Flexible Configuration" 
                    secondary="Easily modify phase durations and transition rules"
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Configurations */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Recent Diet Configurations</Typography>
            <Button
              variant="text"
              onClick={() => navigate("/diet-configurations")}
            >
              View All
            </Button>
          </Box>
          
          {configurations.length > 0 ? (
            <Grid container spacing={2}>
              {configurations.map((config) => (
                <Grid item xs={12} md={6} key={config._id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Typography variant="h6">{config.name}</Typography>
                        <Chip
                          label={config.dietType}
                          color={getDietTypeColor(config.dietType)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {config.description}
                      </Typography>
                      
                      {config.phases && config.phases.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Phases:
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {config.phases.map((phase, index) => (
                              <Chip
                                key={index}
                                label={`${phase.name} (${phase.duration}w)`}
                                color={getPhaseColor(phase.name)}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {config.phaseTransitions && config.phaseTransitions.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Transitions:
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {config.phaseTransitions.length} automatic transition rules configured
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", py: 3 }}>
              No diet configurations found. Create your first configuration to get started.
            </Typography>
          )}
        </Paper>

        {/* System Information */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Cron Job Status
              </Typography>
              <Typography variant="body2">
                {systemStatus.cronActive ? "Active" : "Inactive"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Last Run
              </Typography>
              <Typography variant="body2">
                {systemStatus.lastRun ? new Date(systemStatus.lastRun).toLocaleString() : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Next Scheduled Run
              </Typography>
              <Typography variant="body2">
                {systemStatus.nextRun ? new Date(systemStatus.nextRun).toLocaleString() : "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Layout>
  );
};

export default DietSystemOverview;


